import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import Button from "../button";
import { ContractStatus, ContractStatusList } from "@app/constants";
import { useState } from "react";
import { CustomSelect } from "../select/custom-select";
import ChevronDownArrow from "../select/chevron-down";
import Input from "../input";
import BookService from "@app/services/http/book.service";
import { addToast } from "../toast/toast.service";

interface ContractStatusModalProps {
  contractStatus: number;
  contractLink: string | null;
  contractAnswerOptions?: string;
  bookId: number;
  onStatusUpdated?: () => void;
}

function ContractStatusModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<ContractStatusModalProps>) {
  const {
    contractStatus,
    contractLink,
    contractAnswerOptions,
    bookId,
    onStatusUpdated,
  } = portalData || {};

  const [selectedStatus, setSelectedStatus] = useState(
    contractStatus || ContractStatus.NO_CONTRACT
  );
  const [linkValue, setLinkValue] = useState(contractLink || "");
  const [answerOptionsValue, setAnswerOptionsValue] = useState(
    contractAnswerOptions || ""
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const handleConfirmUpdate = async () => {
    if (!bookId) {
      addToast({ text: "Không tìm thấy ID sách", status: "inValid" });

      return;
    }

    setIsUpdating(true);
    try {
      await BookService.updateContract(
        bookId,
        selectedStatus,
        linkValue.trim() || null,
        answerOptionsValue.trim() || null
      );

      addToast({
        text: "Cập nhật trạng thái hợp đồng thành công",
        status: "valid",
      });

      if (onStatusUpdated) {
        onStatusUpdated();
      }

      portalDialogRef.close();
    } catch (error) {
      addToast({
        text: "Có lỗi xảy ra khi cập nhật trạng thái",
        status: "inValid",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal onCancel={portalDialogRef.close} buttonCancelInChildren={true}>
      <div className="w-[600px] py-7 px-6">
        <div className="text-xl text-center font-semibold mb-6 text-gray-800">
          Thông tin trạng thái hợp đồng
        </div>

        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="text-sm font-medium text-gray-700 mb-3">
            Trạng thái hợp đồng:
          </div>

          <div className="mb-4">
            <CustomSelect
              options={ContractStatusList}
              value={selectedStatus}
              onChange={(value) => setSelectedStatus(value as number)}
              className="bg-white !h-[44px] rounded-lg border border-gray-300"
              optionContainerClassName="mt-1 rounded-b-lg max-h-48 overflow-y-auto"
              optionClassName="px-3 py-2 border-b border-solid"
              labelSelectedClassName="text-gray-700 !mx-3"
              placeholder="Chọn trạng thái"
              arrow={<ChevronDownArrow />}
              hasBgColorHover
            />
          </div>

          <div className="mb-4">
            <div className="text-sm font-medium text-gray-600 mb-2">
              Link hợp đồng (tùy chọn):
            </div>
            <Input
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder="Nhập link hợp đồng..."
              className="w-full"
            />
          </div>

          <div className="mb-4">
            <div className="text-sm font-medium text-gray-600 mb-2">
              Tùy chọn trả lời (tùy chọn):
            </div>
            <Input
              value={answerOptionsValue}
              onChange={(e) => setAnswerOptionsValue(e.target.value)}
              placeholder="Nhập tùy chọn trả lời..."
              className="w-full"
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            size="xs"
            label="Hủy"
            theme="secondary"
            width={80}
            className="!h-[36px] !leading-[36px]"
            onClick={portalDialogRef.close}
            disabled={isUpdating}
          />
          <Button
            size="xs"
            label={isUpdating ? "Đang cập nhật..." : "Xác nhận"}
            width={120}
            className="!h-[36px] !leading-[36px]"
            onClick={handleConfirmUpdate}
            disabled={isUpdating}
          />
        </div>
      </div>
    </Modal>
  );
}

export default ContractStatusModal;
